import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { baseurl } from '../baseurl';
import { 
  Bell, Check, AlertTriangle, Info, 
  ShieldAlert, MessageSquare, Layout, 
  Wallet, UserPlus, CheckCheck 
} from 'lucide-react';

const NotificationCompo = () => {
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  
  const notifRef = useRef(null);
  const userToken = localStorage.getItem('userToken');

  // --- API: Fetch Notifications ---
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${baseurl}getUserNotifications`, {
        headers: { Authorization: `Bearer ${userToken}` }
      });

      console.log("res", res);
      
      if (res.data.success) {
        setNotifications(res.data.notifications);
        setUnreadCount(res.data.unreadCount);
      }
    } catch (err) {
      console.error("Error fetching notifications:", err);
    } finally {
      setLoading(false);
    }
  };

  // --- API: Mark Single as Read ---
  const handleMarkAsRead = async (id) => {
    try {
      const res = await axios.post(`${baseurl}markNotificationRead`, 
        { notificationId: id, markAll: false }, 
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      if (res.data.success) fetchNotifications();
    } catch (err) {
      console.error("Error marking as read:", err);
    }
  };

  // --- API: Mark All as Read ---
  const handleMarkAllAsRead = async (e) => {
    e.stopPropagation(); // Prevent dropdown from closing
    try {
      const res = await axios.post(`${baseurl}markNotificationRead`, 
        { markAll: true }, 
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      if (res.data.success) fetchNotifications();
    } catch (err) {
      console.error("Error marking all as read:", err);
    }
  };

  useEffect(() => {
    if (userToken) fetchNotifications();
  }, [userToken]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) setIsNotifOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const getIcon = (iconName) => {
    const icons = {
      'check-circle': <Check size={14} />,
      'alert-circle': <AlertTriangle size={14} />,
      'wallet': <Wallet size={14} />,
      'layout': <Layout size={14} />,
      'message-square': <MessageSquare size={14} />,
      'shield-alert': <ShieldAlert size={14} />,
      'user-plus': <UserPlus size={14} />
    };
    return icons[iconName] || <Info size={14} />;
  };

  return (
    <div className="dropdown" ref={notifRef}>
      {/* Bell Icon with Badge */}
      <div className="notification-icon position-relative" onClick={() => setIsNotifOpen(!isNotifOpen)} style={{ cursor: 'pointer' }}>
        <Bell className="text-white" size={20} />
        {unreadCount > 0 && (
          <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger" style={{ fontSize: '10px' }}>
            {unreadCount}
          </span>
        )}
      </div>

      {/* Dropdown Menu */}
      <div className={`dropdown-menu dropdown-menu-end shadow-lg border-0 p-0 mt-2 ${isNotifOpen ? 'show' : ''}`}
           style={{ 
             display: isNotifOpen ? 'block' : 'none', 
             position: 'absolute', 
             right: 0, 
             width: '350px', 
             borderRadius: '12px',
             overflow: 'hidden' ,
             zIndex: "999999",
           }}>
        
        {/* Header */}
        <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-center bg-white">
          <h6 className="mb-0 fw-bold text-dark">Notifications</h6>
          {unreadCount > 0 && (
            <button 
              onClick={handleMarkAllAsRead}
              className="btn btn-sm btn-light text-primary fw-semibold d-flex align-items-center gap-1"
              style={{ fontSize: '11px', borderRadius: '20px' }}>
              <CheckCheck size={14} /> Mark all read
            </button>
          )}
        </div>

        {/* List Area */}
        <div className="notif-scroll-area" style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {loading ? (
            <div className="p-5 text-center"><div className="spinner-border spinner-border-sm text-primary"></div></div>
          ) : notifications.length === 0 ? (
            <div className="p-5 text-center text-muted small">No notifications found.</div>
          ) : (
            notifications.map((notif) => (
              <div 
                key={notif.id} 
                className={`p-3 border-bottom d-flex align-items-start gap-3 transition-all`}
                style={{ 
                  backgroundColor: notif.is_read ? '#ffffff' : '#f8f9fa', // Read: White, Unread: Light Grey
                  cursor: 'default'
                }}
              >
                {/* Icon Column */}
                <div className={`rounded-circle d-flex align-items-center justify-content-center flex-shrink-0 ${notif.priority === 'high' ? 'bg-danger text-white' : 'bg-primary-light text-primary'}`}
                     style={{ width: '32px', height: '32px' }}>
                  {getIcon(notif.icon)}
                </div>

                {/* Content Column */}
                <div className="flex-grow-1">
                  <div className="d-flex justify-content-between align-items-start">
                    <span className={`small fw-bold ${notif.is_read ? 'text-secondary' : 'text-dark'}`}>{notif.title}</span>
                    {!notif.is_read && (
                      <button 
                        onClick={() => handleMarkAsRead(notif.id)}
                        className="btn p-0 text-primary border-0 bg-transparent"
                        title="Mark as read" style={{fontSize: ".78rem"}}
                      >
                        <Check size={16} /> Mark As Read
                      </button>
                    )}
                  </div>
                  <p className="text-muted mb-1" style={{ fontSize: '12px', lineHeight: '1.4' }}>{notif.message}</p>
                  <span className="text-uppercase fw-bold text-muted" style={{ fontSize: '9px', letterSpacing: '0.5px' }}>
                    {new Date(notif.created_at).toLocaleDateString()} • {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="bg-light p-2 text-center">
           <small className="text-muted">Stay updated with your latest activities</small>
        </div>
      </div>
    </div>
  );
};

export default NotificationCompo;